import type { Workout } from '@shared/types';
import { formatPace } from './dateUtils';

/**
 * Calculate a training summary based on workout structure
 *
 * Rules:
 * - Rest days: "Rest day"
 * - Workouts with repeat/interval steps: show intervals (e.g. "5x 3min @ 4:00/km")
 * - All other workouts (no repeats): show total duration + distance if available
 */
export const calculateTrainingSummary = (workout: Workout): string => {
  if (!workout || !workout.steps || workout.steps.length === 0) {
    return '';
  }

  // Handle rest days
  if (workout.name.toLowerCase() === 'rest') {
    return 'Rest day';
  }

  const steps = workout.steps;
  const isCycling = workout.sportType === 'cycling';

  // Find all steps with repeat property
  const repeatSteps = steps.filter(step => step.repeat && step.repeat > 1);

  // Workouts with repeat steps: show interval summary + total duration
  if (repeatSteps.length > 0) {
    const mainRepeatStep = repeatSteps[0];
    const totalDurSuffix =
      workout.total_duration > 0
        ? ` (${formatDurationForSummary(workout.total_duration * 60)})`
        : '';

    if (mainRepeatStep.steps && mainRepeatStep.steps.length > 0) {
      const intervalStep =
        mainRepeatStep.steps.find(s => (s.zone || 2) >= 4) || mainRepeatStep.steps[0];

      if (intervalStep.duration) {
        if (isCycling && intervalStep.speed) {
          return `${mainRepeatStep.repeat}x ${formatDurationForSummary(intervalStep.duration)} @ ${intervalStep.speed.toFixed(1)}km/h${totalDurSuffix}`;
        } else if (intervalStep.pace) {
          return `${mainRepeatStep.repeat}x ${formatDurationForSummary(intervalStep.duration)} @ ${formatPace(intervalStep.pace)}/km${totalDurSuffix}`;
        }
        return `${mainRepeatStep.repeat}x ${formatDurationForSummary(intervalStep.duration)}${totalDurSuffix}`;
      }
    }

    if (mainRepeatStep.duration) {
      if (isCycling && mainRepeatStep.speed) {
        return `${mainRepeatStep.repeat}x ${formatDurationForSummary(mainRepeatStep.duration)} @ ${mainRepeatStep.speed.toFixed(1)}km/h${totalDurSuffix}`;
      } else if (mainRepeatStep.pace) {
        return `${mainRepeatStep.repeat}x ${formatDurationForSummary(mainRepeatStep.duration)} @ ${formatPace(mainRepeatStep.pace)}/km${totalDurSuffix}`;
      }
    }
  }

  // No repeat steps: show total duration (total_duration is in minutes, convert to seconds for formatter)
  if (workout.total_duration > 0) {
    const durationStr = formatDurationForSummary(workout.total_duration * 60);
    if ((workout.total_distance || 0) > 0) {
      return `${durationStr}`;
    }
    return durationStr;
  }

  // Fallback to distance or name
  if ((workout.total_distance || 0) > 0) {
    return `${formatDistanceForSummary(workout.total_distance || 0)}`;
  }

  return workout.name;
};

/**
 * Format distance for summary display
 */
const formatDistanceForSummary = (km: number): string => {
  return km % 1 === 0 ? `${km}km` : `${km.toFixed(1)}km`;
};

/**
 * Format duration for summary display (converts seconds to hours/minutes)
 * - < 60min: shows as "45min"
 * - >= 60min with no remaining minutes: shows as "1h", "2h"
 * - >= 60min with remaining minutes: shows as "1h30", "2h15"
 */
const formatDurationForSummary = (seconds: number): string => {
  const totalMinutes = Math.floor(seconds / 60);

  // Less than 60 minutes - show as minutes
  if (totalMinutes < 60) {
    const remainingSeconds = seconds % 60;
    if (remainingSeconds === 0) {
      return `${totalMinutes}min`;
    }
    // For non-whole minutes, show as decimal
    return `${(seconds / 60).toFixed(1)}min`;
  }

  // 60 minutes or more - show as hours and minutes
  const hours = Math.floor(totalMinutes / 60);
  const remainingMinutes = totalMinutes % 60;

  if (remainingMinutes === 0) {
    return `${hours}h`;
  }

  return `${hours}h${remainingMinutes.toString().padStart(2, '0')}`;
};
